import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip';

interface EllipsisTextProps {
  children: string;
  className?: string;
  onClick?: () => void;
  /**
   * How many lines the text may fill before it is cut off. Defaults to 1, which is right where the
   * text sits next to something else on the same line (a heading, a tag, a row of buttons). In a
   * table cell 2–3 lines usually read better: names are what a row is identified by, and the extra
   * lines cost a little row height rather than the text itself.
   */
  lines?: number;
}

/** Tailwind cannot generate a class from a variable, so the supported clamps are spelled out. */
const LINE_CLAMP_CLASS: Record<number, string> = {
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
};

/**
 * Text that ends in an ellipsis once it runs out of room, with the full text on hover.
 *
 * The tooltip is attached only while the text really is cut off — otherwise every short value would
 * pop a tooltip repeating what is already fully on screen, which reads as noise. Whether it is cut
 * off is a layout fact, so it is measured after paint and re-measured whenever the element resizes:
 * width for a single line, height once it is allowed to wrap over several.
 *
 * Needs something to truncate *against*: an ancestor with a real width, and `min-w-0` on any flex
 * parents in between (a flex item defaults to min-width:auto and so refuses to shrink below its
 * text). In a table, beware that a column is never narrower than its longest unbreakable word, so
 * the cell (or the table's layout) has to bound it — see `ResponsiveExpeditionList`'s name cell.
 *
 * Related but different: `ringil3/BreadCrumb/EllipsisMiddleText` cuts the *middle* out of a label by
 * character count and swaps in the full text on hover, for breadcrumb segments.
 */
const EllipsisText: React.FC<EllipsisTextProps> = ({ children, className, onClick, lines = 1 }) => {
  const elementId = `ellipsis_${useId().replace(/:/g, '-')}`;
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  const multiline = lines > 1;

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    // A single line overflows sideways; a clamped block overflows downwards.
    const measure = () => setTruncated(multiline ? element.scrollHeight > element.clientHeight + 1 : element.scrollWidth > element.clientWidth + 1);
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [children, multiline]);

  // `break-words` only where wrapping is allowed: it lets a long unbreakable word run over the lines
  // it is given instead of being cut after the first few characters. No `block` in the clamped case —
  // line-clamp brings its own display (-webkit-box) and `block` would override it, silently leaving
  // the text unclamped.
  const clampClass = multiline ? `${LINE_CLAMP_CLASS[lines] ?? 'line-clamp-2'} break-words` : 'block truncate';

  return (
    <>
      <span ref={ref} id={elementId} className={`${clampClass} ${className ?? ''}`} onClick={onClick}>
        {children}
      </span>
      {truncated && <CustomTooltip targetSelector={`#${elementId}`} content={children} maxWidth="32rem" />}
    </>
  );
};

export default EllipsisText;
